import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  collection, 
  getDocs, 
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { functions } from '../../services/firebase';
import { httpsCallable } from 'firebase/functions';
import { Plus, Edit2, Trash2, Mail, UserCheck, UserX, Phone, MessageCircle, ExternalLink, KeyRound } from 'lucide-react';
import LoadingState from '../shared/LoadingState';
import {
  COLLABORATOR_TYPES,
  DEFAULT_COLLABORATOR_TYPES,
  getCollaboratorTypesLabel,
  getPermissionsForCollaboratorTypes,
  normalizeCollaboratorTypes
} from '../../utils/collaboratorPermissions';
import {
  buildCollaboratorAccessWhatsAppLink,
  buildCollaboratorContactWhatsAppLink,
  generateTemporaryPassword
} from '../../utils/collaboratorAccess';
import './Collaborators.css';

const Collaborators = () => {
  const { userData } = useAuth();
  const [collaborators, setCollaborators] = useState([]);
  const [establishments, setEstablishments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [currentCollaborator, setCurrentCollaborator] = useState(null);
  const [createdAccess, setCreatedAccess] = useState(null);
  const [resettingCollaboratorId, setResettingCollaboratorId] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    accessRole: 'collaborator',
    collaboratorTypes: DEFAULT_COLLABORATOR_TYPES,
    establishmentIds: [],
    active: true
  });

  useEffect(() => {
    if (userData?.companyId) {
      loadCollaborators();
    }
  }, [userData]);

  const loadCollaborators = async () => {
    try {
      setLoading(true);
      const collabRef = collection(db, `companies/${userData.companyId}/collaborators`);
      const establishmentsRef = collection(db, `companies/${userData.companyId}/establishments`);
      const [snapshot, establishmentsSnapshot] = await Promise.all([
        getDocs(collabRef),
        getDocs(establishmentsRef)
      ]);
      
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setCollaborators(data);
      setEstablishments(establishmentsSnapshot.docs
        .map(item => ({ id: item.id, ...item.data() }))
        .filter(item => item.active !== false && ['bar', 'restaurant', 'kiosk'].includes(item.type))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      );
    } catch (error) {
      console.error('Erro ao carregar colaboradores:', error);
      alert('Erro ao carregar colaboradores');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone) {
      alert('Preencha todos os campos obrigatórios');
      return;
    }

    setSaving(true);
    try {
      if (currentCollaborator) {
        // Atualizar colaborador existente
        const collabRef = doc(db, `companies/${userData.companyId}/collaborators/${currentCollaborator.id}`);
        const collaboratorTypes = normalizeCollaboratorTypes(formData.collaboratorTypes);
        const permissions = getPermissionsForCollaboratorTypes(collaboratorTypes);
        await updateDoc(collabRef, {
          userName: formData.name,
          phone: formData.phone,
          collaboratorTypes,
          collaboratorType: collaboratorTypes[0],
          permissions,
          establishmentIds: formData.establishmentIds,
          establishments: establishments
            .filter(establishment => formData.establishmentIds.includes(establishment.id))
            .map(establishment => ({ id: establishment.id, name: establishment.name, type: establishment.type })),
          active: formData.active,
          updatedAt: serverTimestamp(),
          updatedBy: userData.email
        });
        
        // Atualizar também no documento do usuário
        if (currentCollaborator.userId) {
          const userRef = doc(db, 'users', currentCollaborator.userId);
          await updateDoc(userRef, {
            name: formData.name,
            phone: formData.phone,
            collaboratorTypes,
            collaboratorType: collaboratorTypes[0],
            permissions,
            establishmentIds: formData.establishmentIds,
            establishments: establishments
              .filter(establishment => formData.establishmentIds.includes(establishment.id))
              .map(establishment => ({ id: establishment.id, name: establishment.name, type: establishment.type })),
            active: formData.active,
            updatedAt: serverTimestamp()
          });
        }
        
        alert('Colaborador atualizado com sucesso!');
      } else {
        // Criar novo colaborador via Cloud Function
        const password = formData.password || generateTemporaryPassword();
        const accessRole = formData.accessRole === 'admin' ? 'admin' : 'collaborator';
        const collaboratorTypes = normalizeCollaboratorTypes(formData.collaboratorTypes);
        const permissions = getPermissionsForCollaboratorTypes(collaboratorTypes);
        
        // 1. Criar colaborador via callable (usa SDK, evita CORS)
        let createdUid = null;
        try {
          const createCallable = httpsCallable(functions, 'createCollaboratorCallable');
          const res = await createCallable({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            password: password,
            companyId: userData.companyId,
            companyName: userData.companyName || 'Empresa',
            accessRole,
            collaboratorTypes,
            collaboratorType: collaboratorTypes[0],
            permissions,
            establishmentIds: formData.establishmentIds
          });

          if (!res || !res.data || !res.data.success) {
            throw new Error((res && res.data && res.data.error) || 'Erro ao criar colaborador');
          }
          createdUid = res.data.uid || res.data.userId || null;
        } catch (err) {
          if (['permission-denied', 'already-exists', 'invalid-argument', 'unauthenticated'].includes(err?.code)) {
            throw new Error(err.message || 'Erro ao criar colaborador');
          }

          // fallback para a função via emailService (que também tenta callable then fetch)
          const createResult = await (await import('../../services/emailService')).createCollaborator({
            name: formData.name,
            email: formData.email,
            password,
            collaboratorEmail: formData.email,
            collaboratorName: formData.name,
            phone: formData.phone,
            companyId: userData.companyId,
            companyName: userData.companyName || 'Empresa',
            accessRole,
            collaboratorTypes,
            collaboratorType: collaboratorTypes[0],
            permissions,
            establishmentIds: formData.establishmentIds,
            temporaryPassword: password,
            createdBy: userData.email
          });

          if (!createResult.success) {
            throw new Error(createResult.error || 'Erro ao criar colaborador');
          }
          createdUid = createResult.data?.uid || createResult.data?.userId || null;
        }

        if (createdUid && accessRole !== 'admin') {
          const selectedEstablishments = establishments
            .filter(establishment => formData.establishmentIds.includes(establishment.id))
            .map(establishment => ({ id: establishment.id, name: establishment.name, type: establishment.type }));

          await updateDoc(doc(db, 'users', createdUid), {
            phone: formData.phone,
            mustChangePassword: true,
            collaboratorTypes,
            collaboratorType: collaboratorTypes[0],
            permissions,
            establishmentIds: formData.establishmentIds,
            establishments: selectedEstablishments,
            updatedAt: serverTimestamp()
          });

          const createdCollabSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/collaborators`));
          const createdCollab = createdCollabSnapshot.docs.find(item => item.data().userId === createdUid || item.data().userEmail === formData.email);
          if (createdCollab) {
            await updateDoc(doc(db, `companies/${userData.companyId}/collaborators`, createdCollab.id), {
              collaboratorTypes,
              collaboratorType: collaboratorTypes[0],
              phone: formData.phone,
              permissions,
              establishmentIds: formData.establishmentIds,
              establishments: selectedEstablishments,
              updatedAt: serverTimestamp()
            });
          }
        }
        
        setCreatedAccess({
          title: 'Acesso criado',
          statusText: 'Acesso pronto para compartilhar pelo WhatsApp.',
          collaboratorName: formData.name,
          companyName: userData.companyName || 'sua empresa',
          email: formData.email,
          phone: formData.phone,
          password,
          accessLabel: accessRole === 'admin' ? 'Administrador' : getCollaboratorTypesLabel(collaboratorTypes),
          loginUrl: `${window.location.origin}/login`
        });
      }
      
      setShowModal(false);
      resetForm();
      loadCollaborators();
    } catch (error) {
      console.error('Erro ao salvar colaborador:', error);
      alert(`Erro ao salvar colaborador: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (collaborator) => {
    if (!window.confirm(`Tem certeza que deseja remover ${collaborator.userName}?`)) {
      return;
    }

    try {
      const collabRef = doc(db, `companies/${userData.companyId}/collaborators/${collaborator.id}`);
      await deleteDoc(collabRef);
      
      alert('Colaborador removido com sucesso!');
      loadCollaborators();
    } catch (error) {
      console.error('Erro ao remover colaborador:', error);
      alert('Erro ao remover colaborador');
    }
  };

  const toggleActive = async (collaborator) => {
    try {
      const collabRef = doc(db, `companies/${userData.companyId}/collaborators/${collaborator.id}`);
      await updateDoc(collabRef, {
        active: !collaborator.active,
        updatedAt: serverTimestamp(),
        updatedBy: userData.email
      });
      
      loadCollaborators();
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      alert('Erro ao atualizar status');
    }
  };

  const sendCollaboratorWhatsApp = (collaborator) => {
    const whatsappLink = buildCollaboratorContactWhatsAppLink({
      phone: collaborator.phone,
      collaboratorName: collaborator.userName,
      companyName: userData.companyName || 'sua empresa',
      email: collaborator.userEmail,
      loginUrl: `${window.location.origin}/login`,
      accessLabel: collaborator.role === 'admin'
        ? 'Administrador'
        : getCollaboratorTypesLabel(collaborator.collaboratorTypes || collaborator.collaboratorType)
    });

    if (!whatsappLink) {
      alert('Cadastre um WhatsApp válido para este colaborador.');
      return;
    }
    window.open(whatsappLink, '_blank', 'noopener,noreferrer');
  };

  const resetCollaboratorPassword = async (collaborator) => {
    if (!collaborator.userId) {
      alert('Este colaborador não possui um usuário de acesso vinculado.');
      return;
    }
    if (!window.confirm(`Redefinir a senha de ${collaborator.userName}? A senha atual deixará de funcionar.`)) {
      return;
    }

    const temporaryPassword = generateTemporaryPassword();
    setResettingCollaboratorId(collaborator.id);
    try {
      const resetPassword = httpsCallable(functions, 'resetCollaboratorPasswordCallable');
      const result = await resetPassword({
        companyId: userData.companyId,
        collaboratorId: collaborator.id,
        userId: collaborator.userId,
        temporaryPassword
      });
      if (!result?.data?.success) {
        throw new Error('Não foi possível redefinir a senha');
      }

      setCreatedAccess({
        title: 'Senha redefinida',
        statusText: 'Nova senha temporária pronta para envio pelo WhatsApp.',
        collaboratorName: collaborator.userName,
        companyName: userData.companyName || 'sua empresa',
        email: collaborator.userEmail,
        phone: collaborator.phone || '',
        password: temporaryPassword,
        accessLabel: collaborator.role === 'admin'
          ? 'Administrador'
          : getCollaboratorTypesLabel(collaborator.collaboratorTypes || collaborator.collaboratorType),
        loginUrl: `${window.location.origin}/login`
      });
    } catch (error) {
      console.error('Erro ao redefinir senha do colaborador:', error);
      alert(`Erro ao redefinir senha: ${error.message}`);
    } finally {
      setResettingCollaboratorId('');
    }
  };

  const openModal = (collaborator = null) => {
    if (collaborator) {
      setCurrentCollaborator(collaborator);
      setFormData({
        name: collaborator.userName,
        email: collaborator.userEmail,
        phone: collaborator.phone || '',
        password: '',
        accessRole: collaborator.role === 'admin' ? 'admin' : 'collaborator',
        collaboratorTypes: normalizeCollaboratorTypes(collaborator.collaboratorTypes || collaborator.collaboratorType),
        establishmentIds: collaborator.establishmentIds || collaborator.establishments?.map(item => item.id || item.establishmentId).filter(Boolean) || [],
        active: collaborator.active
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const resetForm = () => {
    setCurrentCollaborator(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      password: '',
      accessRole: 'collaborator',
      collaboratorTypes: DEFAULT_COLLABORATOR_TYPES,
      establishmentIds: [],
      active: true
    });
  };

  const toggleEstablishment = (establishmentId) => {
    setFormData(current => ({
      ...current,
      establishmentIds: current.establishmentIds.includes(establishmentId)
        ? current.establishmentIds.filter(id => id !== establishmentId)
        : [...current.establishmentIds, establishmentId]
    }));
  };

  const getCollaboratorEstablishmentNames = (collaborator) => {
    if (collaborator.role === 'admin') {
      return 'Todos os pontos de venda';
    }
    if (Array.isArray(collaborator.establishments) && collaborator.establishments.length > 0) {
      return collaborator.establishments.map(item => item.name).join(', ');
    }
    if (Array.isArray(collaborator.establishmentIds) && collaborator.establishmentIds.length > 0) {
      return collaborator.establishmentIds
        .map(id => establishments.find(establishment => establishment.id === id)?.name || id)
        .join(', ');
    }
    return 'Nenhum ponto de venda vinculado';
  };

  const sendCreatedAccessByWhatsApp = () => {
    if (!createdAccess) return;
    const whatsappLink = buildCollaboratorAccessWhatsAppLink(createdAccess);
    if (!whatsappLink) {
      alert('Informe um WhatsApp válido para enviar os acessos.');
      return;
    }
    window.open(whatsappLink, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className="collaborators-container">
        <LoadingState title="Carregando colaboradores" subtitle="Organizando acessos da equipe" />
      </div>
    );
  }

  return (
    <div className="collaborators-container">
      <div className="page-header">
        <div>
          <h1>Colaboradores</h1>
          <p>Gerencie os colaboradores da empresa</p>
        </div>
        <button className="btn-primary" onClick={() => openModal()}>
          <Plus size={20} />
          Novo Colaborador
        </button>
      </div>

      {collaborators.length === 0 ? (
        <div className="empty-state">
          <UserCheck size={48} color="#ccc" />
          <p>Nenhum colaborador cadastrado</p>
          <button className="btn-secondary" onClick={() => openModal()}>
            Adicionar Primeiro Colaborador
          </button>
        </div>
      ) : (
        <div className="collaborators-grid">
          {collaborators.map((collaborator) => (
            <div key={collaborator.id} className="collaborator-card">
              <div className="collaborator-header">
                <div>
                  <h3>{collaborator.userName}</h3>
                  <p className="email">
                    <Mail size={14} />
                    {collaborator.userEmail}
                  </p>
                  {collaborator.phone && (
                    <p className="email">
                      <Phone size={14} />
                      {collaborator.phone}
                    </p>
                  )}
                </div>
                <span className={`status-badge ${collaborator.active ? 'active' : 'inactive'}`}>
                  {collaborator.active ? 'Ativo' : 'Inativo'}
                </span>
              </div>
              
              <div className="collaborator-info">
                <p>
                  <strong>Cadastrado em:</strong> {' '}
                  {collaborator.addedAt?.toDate?.()?.toLocaleDateString('pt-BR')}
                </p>
                {collaborator.addedBy && (
                  <p>
                    <strong>Por:</strong> {collaborator.addedBy}
                  </p>
                )}
                <p>
                  <strong>Perfil:</strong> {collaborator.role === 'admin' ? 'Administrador' : 'Colaborador'}
                </p>
                <p>
                  <strong>Acessos:</strong> {collaborator.role === 'admin'
                    ? 'Acesso administrativo completo'
                    : (collaborator.permissions || getPermissionsForCollaboratorTypes(collaborator.collaboratorTypes || collaborator.collaboratorType)).join(', ')}
                </p>
                <p>
                  <strong>Pontos de venda:</strong> {getCollaboratorEstablishmentNames(collaborator)}
                </p>
              </div>

              <div className="collaborator-actions">
                <button 
                  className="btn-icon" 
                  onClick={() => openModal(collaborator)}
                  title="Editar"
                >
                  <Edit2 size={18} />
                </button>
                <button 
                  className="btn-icon info" 
                  onClick={() => sendCollaboratorWhatsApp(collaborator)}
                  title="Falar pelo WhatsApp"
                >
                  <MessageCircle size={18} />
                </button>
                <button
                  className="btn-icon warning"
                  onClick={() => resetCollaboratorPassword(collaborator)}
                  title="Redefinir senha temporária"
                  disabled={resettingCollaboratorId === collaborator.id}
                >
                  <KeyRound size={18} />
                </button>
                <button 
                  className={`btn-icon ${collaborator.active ? 'danger' : 'success'}`}
                  onClick={() => toggleActive(collaborator)}
                  title={collaborator.active ? 'Desativar' : 'Ativar'}
                >
                  {collaborator.active ? <UserX size={18} /> : <UserCheck size={18} />}
                </button>
                <button 
                  className="btn-icon danger" 
                  onClick={() => handleDelete(collaborator)}
                  title="Remover"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de Cadastro/Edição */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{currentCollaborator ? 'Editar Colaborador' : 'Novo Colaborador'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Nome *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    disabled={currentCollaborator}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>WhatsApp *</label>
                  <input
                    type="tel"
                    inputMode="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="(11) 99999-9999"
                    required
                  />
                </div>

                {!currentCollaborator && (
                  <div className="form-group">
                    <label>Senha Inicial</label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      placeholder="Deixe em branco para gerar automaticamente"
                      minLength="6"
                    />
                    <small>Se ficar em branco, o sistema gera uma senha temporária segura.</small>
                  </div>
                )}

                {!currentCollaborator && ['admin', 'root'].includes(userData?.role) && (
                  <div className="form-group">
                    <label>Perfil de acesso *</label>
                    <div className="collaborator-type-grid">
                      <label className={`collaborator-type-card ${formData.accessRole === 'collaborator' ? 'is-selected' : ''}`}>
                        <input
                          type="radio"
                          name="accessRole"
                          value="collaborator"
                          checked={formData.accessRole === 'collaborator'}
                          onChange={(event) => setFormData({...formData, accessRole: event.target.value})}
                        />
                        <span>Colaborador</span>
                        <small>Recebe somente os acessos selecionados abaixo.</small>
                      </label>
                      <label className={`collaborator-type-card ${formData.accessRole === 'admin' ? 'is-selected' : ''}`}>
                        <input
                          type="radio"
                          name="accessRole"
                          value="admin"
                          checked={formData.accessRole === 'admin'}
                          onChange={(event) => setFormData({...formData, accessRole: event.target.value})}
                        />
                        <span>Administrador</span>
                        <small>Acessa todas as áreas administrativas da empresa.</small>
                      </label>
                    </div>
                  </div>
                )}

                {formData.accessRole !== 'admin' && (
                <div className="form-group">
                  <label>Tipos de colaborador *</label>
                  <div className="collaborator-type-grid">
                    {Object.values(COLLABORATOR_TYPES).map(type => (
                      <label key={type.value} className={`collaborator-type-card ${formData.collaboratorTypes.includes(type.value) ? 'is-selected' : ''}`}>
                        <input
                          type="checkbox"
                          value={type.value}
                          checked={formData.collaboratorTypes.includes(type.value)}
                          onChange={() => {
                            setFormData(current => {
                              const exists = current.collaboratorTypes.includes(type.value);
                              const nextTypes = exists
                                ? current.collaboratorTypes.filter(item => item !== type.value)
                                : [...current.collaboratorTypes, type.value];
                              return {
                                ...current,
                                collaboratorTypes: nextTypes.length > 0 ? nextTypes : DEFAULT_COLLABORATOR_TYPES
                              };
                            });
                          }}
                        />
                        <span>{type.label}</span>
                        <small>{type.description}</small>
                      </label>
                    ))}
                  </div>
                </div>
                )}

                {formData.accessRole !== 'admin' && (formData.collaboratorTypes.includes('pos') || formData.collaboratorTypes.includes('kitchen')) && (
                <div className="form-group">
                  <label>Pontos de venda que pode operar</label>
                  <div className="establishment-checklist">
                    {establishments.map(establishment => (
                      <label key={establishment.id} className="establishment-check">
                        <input
                          type="checkbox"
                          checked={formData.establishmentIds.includes(establishment.id)}
                          onChange={() => toggleEstablishment(establishment.id)}
                        />
                        <span>{establishment.name}</span>
                        <small>{establishment.type === 'bar' ? 'Bar' : establishment.type === 'kiosk' ? 'Quiosque' : 'Restaurante'}</small>
                      </label>
                    ))}
                    {establishments.length === 0 && (
                      <p className="form-hint">Cadastre bares, restaurantes ou quiosques em Pontos de venda antes de limitar o acesso.</p>
                    )}
                  </div>
                  <small>Se nenhum for marcado, o colaborador não verá pedidos nem PDV dos pontos de venda.</small>
                </div>
                )}

                <div className="form-group checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData({...formData, active: e.target.checked})}
                    />
                    Ativo
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Salvando...' : `${currentCollaborator ? 'Atualizar' : 'Criar'} Colaborador`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {createdAccess && (
        <div className="modal-overlay" onClick={() => setCreatedAccess(null)}>
          <div className="modal-content access-share-modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h2>{createdAccess.title || 'Acesso criado'}</h2>
              <button className="modal-close" onClick={() => setCreatedAccess(null)} aria-label="Fechar">×</button>
            </div>
            <div className="modal-body access-share-body">
              <div className="access-share-status">
                <UserCheck size={24} />
                <div>
                  <strong>{createdAccess.collaboratorName}</strong>
                  <span>{createdAccess.statusText || 'Acesso pronto para compartilhar pelo WhatsApp.'}</span>
                </div>
              </div>
              <dl className="access-credentials">
                <div><dt>Link</dt><dd>{createdAccess.loginUrl}</dd></div>
                <div><dt>Usuário</dt><dd>{createdAccess.email}</dd></div>
                <div><dt>Senha temporária</dt><dd>{createdAccess.password}</dd></div>
                <div><dt>Acessos</dt><dd>{createdAccess.accessLabel}</dd></div>
                <div><dt>WhatsApp</dt><dd>{createdAccess.phone}</dd></div>
              </dl>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={() => setCreatedAccess(null)}>Fechar</button>
              <button type="button" className="access-whatsapp-button" onClick={sendCreatedAccessByWhatsApp}>
                <MessageCircle size={19} />
                Enviar acessos
                <ExternalLink size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Collaborators;
